// ═══════════════════════════════════════════════════════════════════════════════
// WritingComposition.jsx — AssessNow writing assessment component
//
// Renders:
//   • Reading passage (left panel)
//   • Writing prompt + text editor (right panel)
//   • Rubric criteria display (before submission)
//   • Score breakdown (after submission)
//
// Uses the 360-IF engine (WritingAnalyzer) for local, private scoring.
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef, useCallback } from 'react';
import WritingAnalyzer from '../engine/WritingAnalyzer';
import { passageContext } from '../utils/readingPassages';

// ── constants ──────────────────────────────────────────────────────────────

const DEFAULT_MIN_WORDS = 20;
const DEFAULT_MAX_WORDS = 500;
const TRAINING_TIMEOUT_MS = 30_000;

// ── component ──────────────────────────────────────────────────────────────

export default function WritingComposition({
  question,         // current question object from assessment JSON
  ttsEnabled,       // text-to-speech enabled flag
  onAnswer,         // (questionId, answer, questionType, rubricResult) => void
  onDraftChange,    // debounced encrypted-server draft persistence
  isSubmitting,     // parent submission lock
  studentResponse,  // pre-existing response (if navigating back)
  draftText = '',   // server-restored unfinished draft
  draftStorageKey,  // session-only tablet fallback (never localStorage)
  minimumTimeComplete = true,
  minimumSeconds = 0,
  registerSubmission,
  HighlightedTTS,   // the TTS wrapper component from AssessNow
}) {
  // ── state ────────────────────────────────────────────────────────────────
  const sessionDraft = (() => {
    if (!draftStorageKey || typeof window === 'undefined') return '';
    try { return window.sessionStorage.getItem(draftStorageKey) || ''; } catch { return ''; }
  })();
  const [text, setText]               = useState(studentResponse || draftText || sessionDraft);
  const [wordCount, setWordCount]     = useState(0);
  const [isScoring, setIsScoring]     = useState(false);
  const [scoringPhase, setScoringPhase] = useState('');
  const [scoringPct, setScoringPct]   = useState(0);
  const [result, setResult]           = useState(null);
  const [engineReady, setEngineReady] = useState(false);
  const [engineError, setEngineError] = useState(null);

  const analyzerRef  = useRef(null);
  const textareaRef  = useRef(null);
  const latestTextRef = useRef(text);
  const dirtyDraftRef = useRef(false);

  // Unpack question fields
  const passage       = passageContext(question);
  const direction     = question?.Direction || question?.Options?.Direction || 'Write a paragraph based on the passage.';
  const questionText  = question?.QuestionText || question?.Options?.QuestionText || question?.prompt || direction;
  const rubricCriteria = question?.RubricCriteria || question?.Options?.RubricCriteria || defaultRubric();
  const questionId    = question?.ItemNumber || question?.id;
  const minimumWords  = Number.isFinite(Number(question?.MinWordCount ?? question?.Options?.MinWordCount))
    ? Math.max(0, Number(question?.MinWordCount ?? question?.Options?.MinWordCount))
    : DEFAULT_MIN_WORDS;
  const maximumWords  = Number.isFinite(Number(question?.MaxWordCount ?? question?.Options?.MaxWordCount))
    ? Math.max(minimumWords, Number(question?.MaxWordCount ?? question?.Options?.MaxWordCount))
    : DEFAULT_MAX_WORDS;
  const maximumWordPolicy = String(
    question?.MaximumWordPolicy ?? question?.MaxWordPolicy ??
    question?.Options?.MaximumWordPolicy ?? question?.Options?.MaxWordPolicy ?? 'soft'
  ).normalize('NFKC').trim().toLowerCase();
  const maximumWordGracePercent = Math.max(0, Math.min(100, Number(
    question?.MaximumWordGracePercent ?? question?.MaxWordGracePercent ??
    question?.Options?.MaximumWordGracePercent ?? question?.Options?.MaxWordGracePercent ?? 10
  ) || 0));
  const enforcedMaximum = maximumWordPolicy === 'grace'
    ? maximumWords + Math.ceil(maximumWords * maximumWordGracePercent / 100)
    : maximumWords;
  const maximumBlocked = (
    (maximumWordPolicy === 'strict' && wordCount > maximumWords) ||
    (maximumWordPolicy === 'grace' && wordCount > enforcedMaximum)
  );

  // ── train engine on passage mount ────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    const analyzer = new WritingAnalyzer();
    analyzerRef.current = analyzer;

    (async () => {
      try {
        // Train on passage with question context
        await analyzer.trainOnPassage(passage, {
          questionText: questionText,  // Pass question for requirement extraction
          epochs: 60,
          onProgress: (phase, pct) => {
            if (cancelled) return;
            setScoringPhase(phase);
            setScoringPct(pct);
          }
        });
        if (!cancelled) {
          setEngineReady(true);
          setScoringPhase('');
          console.log(`[WritingComposition] Engine ready. Question type: ${analyzer.getQuestionType()}, Requirements: ${analyzer.getQuestionRequirements().length}`);
        }
      } catch (err) {
        console.error('[WritingComposition] Engine training failed:', err);
        if (!cancelled) {
          setEngineError(err.message);
          setEngineReady(true); // Fall back to heuristic scoring
        }
      }
    })();

    return () => { cancelled = true; };
  }, [passage, questionText]);

  // ── word count ───────────────────────────────────────────────────────────

  useEffect(() => {
    const words = text.trim().split(/\s+/).filter(w => w.length > 0);
    setWordCount(words.length);
  }, [text]);

  // Keep an immediate session-only copy, then send a debounced checkpoint to
  // the encrypted server. The cleanup flush protects a draft when the student
  // navigates away just before the debounce fires.
  useEffect(() => {
    latestTextRef.current = text;
    if (!dirtyDraftRef.current) return undefined;
    if (draftStorageKey) {
      try {
        if (text) window.sessionStorage.setItem(draftStorageKey, text);
        else window.sessionStorage.removeItem(draftStorageKey);
      } catch {
        // Storage may be disabled; the server checkpoint remains authoritative.
      }
    }
    const timeoutId = window.setTimeout(() => {
      if (!dirtyDraftRef.current) return;
      const pending = onDraftChange?.(text);
      pending?.then?.(() => {
        dirtyDraftRef.current = false;
      })?.catch?.((error) => {
        console.warn('[WritingComposition] Draft checkpoint delayed:', error);
      });
    }, 1500);
    return () => window.clearTimeout(timeoutId);
  }, [text, draftStorageKey, onDraftChange]);

  useEffect(() => () => {
    if (dirtyDraftRef.current) {
      onDraftChange?.(latestTextRef.current)?.catch?.(() => undefined);
    }
  }, [onDraftChange]);

  // ── handle text change ───────────────────────────────────────────────────

  const handleChange = useCallback((e) => {
    dirtyDraftRef.current = true;
    setText(e.target.value);
    setResult(null); // clear previous result on edit
  }, []);

  // ── score & submit ───────────────────────────────────────────────────────

  const handleScore = useCallback(async () => {
    // Only check local state - don't let parent's isSubmitting block us
    if (wordCount < minimumWords || maximumBlocked || isScoring || !minimumTimeComplete) {
      console.log('[WritingComposition] handleScore blocked:', { wordCount, minimumWords, maximumWords, maximumWordPolicy, enforcedMaximum, isScoring, minimumTimeComplete });
      return;
    }

    setIsScoring(true);
    setScoringPhase('scoring');
    console.log('[WritingComposition] Starting scoring...');

    let rubricResult = null;

    try {
      if (analyzerRef.current) {
        // Full 360-IF when trained; the same analyzer supplies deterministic
        // heuristic profiles if passage training was unavailable.
        rubricResult = analyzerRef.current.analyzeWriting(text, rubricCriteria, questionText);
        console.log(`[WritingComposition] Scored. Type: ${rubricResult.questionType}, Requirements met: ${rubricResult.requirementsMet?.length || 0}, missing: ${rubricResult.requirementsMissing?.length || 0}`);
      } else {
        // Defensive fallback if component initialization did not create an analyzer.
        rubricResult = heuristicScore(text, rubricCriteria);
      }
    } catch (err) {
      console.error('[WritingComposition] Scoring error:', err);
      setEngineError(err.message);
      // Fallback to heuristic on error
      try {
        const fallbackAnalyzer = new WritingAnalyzer();
        rubricResult = fallbackAnalyzer.analyzeWriting(text, rubricCriteria, questionText);
      } catch (fallbackErr) {
        console.error('[WritingComposition] Heuristic fallback also failed:', fallbackErr);
        // Create minimal result to allow proceeding
        rubricResult = {
          scores: {},
          total: 0,
          maxPossible: rubricCriteria.reduce((sum, c) => sum + (c.weight || 1), 0),
          percentage: 0,
          evidenceScore: 0
        };
        for (const c of rubricCriteria) {
          rubricResult.scores[c.name] = { rawScore: 0, points: 0, maxPoints: c.weight || 1, feedback: 'Scoring error occurred.' };
        }
      }
    }

    console.log('[WritingComposition] Scoring complete, calling onAnswer:', { questionId, textLength: text.length, percentage: rubricResult?.percentage });

    let submissionError = null;
    // Report to parent (useAssessment) — answer + rubric scores
    if (onAnswer) {
      try {
        if (dirtyDraftRef.current && onDraftChange) {
          try {
            await onDraftChange(text);
            dirtyDraftRef.current = false;
          } catch (draftError) {
            console.warn('[WritingComposition] Final draft checkpoint delayed:', draftError);
          }
        }
        const authoritative = await onAnswer(questionId, text, 'writingComposition', rubricResult);
        rubricResult = authoritative?.rubricResult || rubricResult;
        setResult(rubricResult);
        setEngineError(null);
        dirtyDraftRef.current = false;
        if (draftStorageKey) {
          try { window.sessionStorage.removeItem(draftStorageKey); } catch { /* no-op */ }
        }
      } catch (error) {
        console.error('[WritingComposition] Unable to save scored response:', error);
        setResult(null);
        const requirement = error?.code === 'WRITING_RESPONSE_TOO_SHORT' && error?.minimumWords
          ? ` Write at least ${error.minimumWords} words.`
          : error?.code === 'WRITING_RESPONSE_TOO_LONG' && error?.maximumWords
            ? ` Use no more than ${error.maximumWords} words.`
            : '';
        setEngineError(`Your response could not be saved yet.${requirement} Your work is still displayed. Please select Next again.`);
        submissionError = error;
      }
    } else {
      console.warn('[WritingComposition] onAnswer is not defined!');
      setEngineError('Your response could not be saved because the assessment connection is unavailable.');
    }

    setIsScoring(false);
    setScoringPhase('');
    if (submissionError) throw submissionError;
    return rubricResult;
  }, [text, wordCount, minimumWords, maximumWords, maximumBlocked, maximumWordPolicy, enforcedMaximum, isScoring, rubricCriteria, questionId, onAnswer, onDraftChange, minimumTimeComplete, draftStorageKey]);

  useEffect(() => {
    if (!registerSubmission) return undefined;
    registerSubmission({
      submit: handleScore,
      ready: wordCount >= minimumWords && !maximumBlocked && engineReady && minimumTimeComplete,
      wordCount,
      minimumWords
    });
    return undefined;
  }, [registerSubmission, handleScore, wordCount, minimumWords, maximumBlocked, engineReady, minimumTimeComplete]);

  useEffect(() => () => registerSubmission?.(null), [registerSubmission]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div style={styles.container}>

      {/* ── Training Progress (shows while engine trains on passage) ─── */}
      {!engineReady && (
        <div style={styles.trainingBanner}>
          <div style={styles.trainingText}>
            Preparing scorer… {scoringPhase} ({scoringPct}%)
          </div>
          <div style={styles.progressTrack}>
            <div style={{ ...styles.progressFill, width: `${scoringPct}%` }} />
          </div>
        </div>
      )}

      {/* ── Writing Prompt ──────────────────────────────────────────── */}
      <div style={styles.promptCard}>
        {HighlightedTTS ? (
          <HighlightedTTS
            text={direction}
            speechText={question?.DirectionSpeech || ''}
            enabled={ttsEnabled}
            size="medium"
            containerStyle={{}}
            textStyle={styles.promptText}
            highlightedStyle={{ backgroundColor: '#fef08a' }}
          />
        ) : (
          <p style={styles.promptText}>{direction}</p>
        )}
      </div>

      {/* ── Rubric Preview (before submission) ─────────────────────── */}
      {!result && (
        <div style={styles.rubricPreview}>
          <div style={styles.rubricTitle}>Scoring Criteria</div>
          <div style={styles.rubricChips}>
            {rubricCriteria.map(c => (
              <span key={c.name} style={styles.rubricChip}>
                {c.name} <span style={styles.chipWeight}>×{c.weight}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── Text Editor ────────────────────────────────────────────── */}
      <div style={styles.editorContainer}>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleChange}
          placeholder="Start writing here…"
          disabled={isScoring || isSubmitting}
          style={styles.textarea}
          rows={10}
          aria-label="Student writing response"
        />
        <div style={styles.editorFooter}>
          <span style={{
            ...styles.wordCount,
            color: wordCount < minimumWords || maximumBlocked ? '#ef4444' : wordCount > maximumWords ? '#b45309' : '#16a34a'
          }}>
            {wordCount} word{wordCount !== 1 ? 's' : ''}
            {wordCount < minimumWords && ` — Keep writing: add ${minimumWords - wordCount} more word${minimumWords - wordCount === 1 ? '' : 's'}. Next unlocks at ${minimumWords} words.`}
            {wordCount >= minimumWords && wordCount <= maximumWords && ` — Ready to submit. Your response meets the ${minimumWords}-word minimum.`}
            {wordCount > maximumWords && !maximumBlocked && ` — ${wordCount - maximumWords} word${wordCount - maximumWords === 1 ? '' : 's'} over the suggested ${maximumWords}-word target. You may revise for concision or submit and score now.`}
            {maximumBlocked && ` — Revise before submitting: remove ${wordCount - enforcedMaximum} word${wordCount - enforcedMaximum === 1 ? '' : 's'}. This item allows at most ${enforcedMaximum} words.`}
          </span>
        </div>
      </div>

      {/* ── Submit / Score Button ───────────────────────────────────── */}
      {!result && (
        minimumSeconds > 0 && !minimumTimeComplete && (
          <div role="status" style={styles.minimumTimeNotice}>
            Keep working. Next unlocks when the minimum writing time is complete.
          </div>
        )
      )}

      {/* ── Results Panel ───────────────────────────────────────────── */}
      {result && (
        <div style={styles.resultsPanel}>
          {result.responseValidity?.valid === false && (
            <div role="alert" style={styles.invalidResponseNotice}>
              <strong>This response could not be meaningfully scored.</strong>{' '}
              {result.responseValidity.message}
            </div>
          )}
          {/* Overall score */}
          <div style={styles.overallScore}>
            <div style={styles.scoreCircle(result.percentage)}>
              <span style={styles.scoreNumber}>{result.percentage}%</span>
            </div>
            <div style={styles.scoreSummary}>
              <div style={styles.scoreTotalLine}>
                {result.total} / {result.maxPossible} points
              </div>
              <div style={styles.scoreLabel}>Overall Score</div>
            </div>
          </div>

          {/* One response, two instructional profiles. These diagnostic
              percentages do not replace the configured holistic rubric. */}
          {result.writingProfiles?.expression && result.writingProfiles?.mechanics && (
            <section style={styles.profileSection} aria-labelledby="writing-skill-profiles-title">
              <div style={styles.profileSectionHeader}>
                <div>
                  <div id="writing-skill-profiles-title" style={styles.profileSectionTitle}>Writing Skill Profiles</div>
                  <div style={styles.profileSectionNote}>Diagnostic views of the same response; the overall rubric score remains unchanged.</div>
                </div>
              </div>
              <div style={styles.profileGrid}>
                {['expression', 'mechanics'].map(profileKey => {
                  const profile = result.writingProfiles[profileKey];
                  const profilePct = profile.percentage;
                  return (
                    <article key={profile.areaId} style={styles.profileCard}>
                      <div style={styles.profileCardHeader}>
                        <span style={styles.profileName}>{profile.areaName}</span>
                        <span style={{
                          ...styles.profilePercentage,
                          color: profilePct === null ? '#64748b' : barColor(profilePct)
                        }}>
                          {profilePct === null ? 'N/A' : `${profilePct}%`}
                        </span>
                      </div>
                      <div style={styles.profileFeedback}>{profile.feedback}</div>
                      <div style={styles.profileDimensions}>
                        {profile.dimensions.map(dimension => (
                          <div key={dimension.id} style={styles.profileDimension}>
                            <div style={styles.profileDimensionHeader}>
                              <span>{dimension.label}</span>
                              <span>{dimension.applicable ? `${dimension.percentage}%` : 'N/A'}</span>
                            </div>
                            {dimension.applicable && (
                              <div style={styles.profileDimensionTrack}>
                                <div style={{
                                  ...styles.profileDimensionFill,
                                  width: `${dimension.percentage}%`,
                                  backgroundColor: barColor(dimension.percentage)
                                }} />
                              </div>
                            )}
                            <div style={styles.profileDimensionFeedback}>{dimension.feedback}</div>
                          </div>
                        ))}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          {/* Per-criterion breakdown */}
          <div style={styles.criteriaGrid}>
            {rubricCriteria.map(c => {
              const s = result.scores[c.name];
              if (!s) return null;
              const pct = c.weight > 0 ? Math.round((s.points / c.weight) * 100) : 0;
              return (
                <div key={c.name} style={styles.criterionCard}>
                  <div style={styles.criterionHeader}>
                    <span style={styles.criterionName}>{c.name}</span>
                    <span style={styles.criterionPoints}>
                      {s.points}/{s.maxPoints}
                    </span>
                  </div>
                  <div style={styles.criterionBar}>
                    <div style={{ ...styles.criterionFill, width: `${pct}%`, backgroundColor: barColor(pct) }} />
                  </div>
                  <div style={styles.criterionFeedback}>{s.feedback}</div>
                </div>
              );
            })}
          </div>

          {/* Evidence score */}
          {result.evidenceScore > 0 && (
            <div style={styles.evidenceNote}>
              Evidence relevance: {Math.round(result.evidenceScore * 100)}%
              {result.evidenceScore < 0.3 && ' — Consider using more details from the passage.'}
            </div>
          )}

          {engineError && (
            <div style={styles.engineWarning}>
              ⚠ Scored with basic analysis (engine note: {engineError})
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DEFAULTS & HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

function defaultRubric() {
  return [
    { name: 'Claim',       weight: 2, description: 'Clear opinion or thesis statement' },
    { name: 'Reasoning',   weight: 4, description: 'Logical reasoning that supports the claim' },
    { name: 'Evidence',    weight: 4, description: 'Text-based evidence from the passage' },
    { name: 'Coherence',   weight: 3, description: 'Logical flow and organisation' },
    { name: 'Conventions', weight: 1, description: 'Grammar, spelling, punctuation' }
  ];
}

/** Heuristic fallback when 360-IF engine is unavailable */
function heuristicScore(text, criteria) {
  return new WritingAnalyzer().analyzeWriting(text, criteria);
}

function barColor(pct) {
  if (pct >= 75) return '#16a34a';
  if (pct >= 50) return '#eab308';
  if (pct >= 25) return '#f97316';
  return '#ef4444';
}

// ═══════════════════════════════════════════════════════════════════════════════
// STYLES  (inline — matches AssessNow's existing pattern)
// ═══════════════════════════════════════════════════════════════════════════════

const styles = {
  container: {
    display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%'
  },

  // Training banner
  trainingBanner: {
    background: 'linear-gradient(135deg, #ede9fe 0%, #e0e7ff 100%)',
    borderRadius: '0.75rem', padding: '0.75rem 1rem',
    border: '1px solid #c4b5fd'
  },
  trainingText: {
    fontSize: '0.875rem', color: '#5b21b6', fontWeight: 600, marginBottom: '0.5rem'
  },
  progressTrack: {
    height: '6px', borderRadius: '3px', backgroundColor: '#ddd6fe', overflow: 'hidden'
  },
  progressFill: {
    height: '100%', borderRadius: '3px',
    background: 'linear-gradient(90deg, #7c3aed, #6d28d9)',
    transition: 'width 0.3s ease'
  },

  // Prompt
  promptCard: {
    backgroundColor: '#f0f9ff', borderRadius: '0.75rem', padding: '1rem 1.25rem',
    border: '1px solid #bae6fd'
  },
  promptText: {
    fontSize: '1.25rem', fontWeight: 600, color: '#0c4a6e', lineHeight: 1.5, margin: 0
  },

  // Rubric preview
  rubricPreview: {
    display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
    padding: '0.5rem 0'
  },
  rubricTitle: {
    fontSize: '0.8rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  rubricChips: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  rubricChip: {
    fontSize: '0.8rem', padding: '0.25rem 0.6rem', borderRadius: '999px',
    backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
    fontWeight: 500, textTransform: 'capitalize'
  },
  chipWeight: { color: '#94a3b8', marginLeft: '0.2rem' },

  // Editor
  editorContainer: {
    border: '2px solid #cbd5e1', borderRadius: '0.75rem', overflow: 'hidden',
    transition: 'border-color 0.2s'
  },
  textarea: {
    width: '100%', border: 'none', outline: 'none', resize: 'vertical',
    padding: '1rem', fontSize: '1.125rem', lineHeight: 1.75,
    fontFamily: '"Georgia", "Times New Roman", serif', color: '#1e293b',
    minHeight: '200px', boxSizing: 'border-box',
    backgroundColor: '#fff'
  },
  editorFooter: {
    display: 'flex', justifyContent: 'flex-end', padding: '0.4rem 0.75rem',
    backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0'
  },
  wordCount: { fontSize: '0.8rem', fontWeight: 600 },
  minimumTimeNotice: {
    padding: '0.65rem 0.8rem', borderRadius: '0.5rem',
    color: '#92400e', backgroundColor: '#fef3c7', border: '1px solid #fde68a',
    fontSize: '0.875rem', fontWeight: 600
  },
  invalidResponseNotice: {
    padding: '0.8rem 0.9rem', borderRadius: '0.55rem',
    color: '#991b1b', backgroundColor: '#fef2f2', border: '1px solid #fecaca',
    fontSize: '0.875rem', lineHeight: 1.45
  },

  // Submit button
  submitBtn: {
    padding: '0.75rem 2rem', fontSize: '1.1rem', fontWeight: 700,
    color: '#fff', backgroundColor: '#2563eb', border: 'none',
    borderRadius: '0.5rem', transition: 'all 0.2s',
    alignSelf: 'flex-end'
  },

  // Results panel
  resultsPanel: {
    backgroundColor: '#f8fafc', borderRadius: '0.75rem', padding: '1.25rem',
    border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '1rem'
  },
  overallScore: {
    display: 'flex', alignItems: 'center', gap: '1rem'
  },
  scoreCircle: (pct) => ({
    width: '72px', height: '72px', borderRadius: '50%', display: 'flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    background: `conic-gradient(${barColor(pct)} ${pct * 3.6}deg, #e2e8f0 0deg)`,
    position: 'relative'
  }),
  scoreNumber: {
    fontSize: '1.1rem', fontWeight: 800, color: '#1e293b',
    backgroundColor: '#fff', borderRadius: '50%', width: '56px', height: '56px',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  scoreSummary: { display: 'flex', flexDirection: 'column' },
  scoreTotalLine: { fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' },
  scoreLabel: { fontSize: '0.8rem', color: '#64748b' },

  // Dual writing profiles
  profileSection: {
    display: 'flex', flexDirection: 'column', gap: '0.75rem',
    padding: '1rem', borderRadius: '0.75rem', backgroundColor: '#fff',
    border: '1px solid #cbd5e1'
  },
  profileSectionHeader: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem'
  },
  profileSectionTitle: { fontSize: '1rem', fontWeight: 800, color: '#0f172a' },
  profileSectionNote: { fontSize: '0.75rem', color: '#64748b', lineHeight: 1.4, marginTop: '0.15rem' },
  profileGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.75rem'
  },
  profileCard: {
    border: '1px solid #e2e8f0', borderRadius: '0.65rem', padding: '0.85rem',
    backgroundColor: '#f8fafc'
  },
  profileCardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem'
  },
  profileName: { fontSize: '0.875rem', fontWeight: 800, color: '#334155' },
  profilePercentage: { fontSize: '1rem', fontWeight: 900 },
  profileFeedback: { fontSize: '0.72rem', color: '#64748b', lineHeight: 1.4, margin: '0.35rem 0 0.65rem' },
  profileDimensions: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  profileDimension: { display: 'flex', flexDirection: 'column', gap: '0.2rem' },
  profileDimensionHeader: {
    display: 'flex', justifyContent: 'space-between', gap: '0.5rem',
    color: '#475569', fontSize: '0.72rem', fontWeight: 700
  },
  profileDimensionTrack: {
    height: '5px', borderRadius: '999px', backgroundColor: '#e2e8f0', overflow: 'hidden'
  },
  profileDimensionFill: { height: '100%', borderRadius: '999px' },
  profileDimensionFeedback: { color: '#64748b', fontSize: '0.68rem', lineHeight: 1.35 },

  // Criteria grid
  criteriaGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '0.75rem'
  },
  criterionCard: {
    backgroundColor: '#fff', borderRadius: '0.5rem', padding: '0.75rem',
    border: '1px solid #e2e8f0'
  },
  criterionHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '0.4rem'
  },
  criterionName: {
    fontSize: '0.875rem', fontWeight: 700, color: '#334155', textTransform: 'capitalize'
  },
  criterionPoints: {
    fontSize: '0.8rem', fontWeight: 700, color: '#64748b'
  },
  criterionBar: {
    height: '6px', borderRadius: '3px', backgroundColor: '#e2e8f0',
    overflow: 'hidden', marginBottom: '0.4rem'
  },
  criterionFill: {
    height: '100%', borderRadius: '3px', transition: 'width 0.4s ease'
  },
  criterionFeedback: {
    fontSize: '0.75rem', color: '#64748b', lineHeight: 1.4
  },

  // Evidence note
  evidenceNote: {
    fontSize: '0.8rem', color: '#6366f1', fontWeight: 500,
    padding: '0.5rem 0.75rem', backgroundColor: '#eef2ff', borderRadius: '0.5rem'
  },

  engineWarning: {
    fontSize: '0.75rem', color: '#92400e', padding: '0.4rem 0.75rem',
    backgroundColor: '#fef3c7', borderRadius: '0.5rem'
  }
};
